import React, { useEffect, useRef } from "react";
import Radar from "radar-sdk-js";
import "radar-sdk-js/dist/radar.css";
import { LocationModel } from "../models/location.model";
import { DELIVERY, GOOGLE_MAP_DEFAULT_ZOOM } from "../constants";
import { IDoorDash } from "../models/tracking";
import { getDoorDashMapCoords } from "../helpers/tracking/doorDashCoords";
import locationMarker from "../../src/images/location/location-marker-blue.svg";
import doordashIcon from "../../src/images/location/doordash.svg";
import homeIcon from "../../src/images/home.svg";

export const MapMarker: any = ({ text }) => (
  <div
    style={{
      display: "inline-flex",
      textAlign: "center",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "100%",
      transform: "translate(-50%, -50%)",
    }}
  >
    {text}
  </div>
);

const OrderTrackingMap: React.FC<{
  storeLocation?: LocationModel;
  customerDetails: {
    first_name: string;
    address: string;
    street_1: string;
    city: string;
    state: string;
    zipcode: string;
  };
  pickUpMethod: string;
  doorDash: IDoorDash;
  containerId: string;
}> = ({
  customerDetails,
  storeLocation,
  pickUpMethod,
  doorDash,
  containerId,
}) => {
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const mapLoadedRef = useRef<boolean>(false);
  const doordashMarkerRef = useRef<any>(null);
  const doorDashRef = useRef(doorDash);

  doorDashRef.current = doorDash;

  const doorDashMapCoords = getDoorDashMapCoords(doorDash);

  const removeDoorDashMarker = () => {
    if (!doordashMarkerRef.current) return;
    doordashMarkerRef.current.remove();
    markersRef.current = markersRef.current.filter(
      (m) => m !== doordashMarkerRef.current
    );
    doordashMarkerRef.current = null;
  };

  const syncDoorDashMarker = (map: any) => {
    if (pickUpMethod !== DELIVERY) {
      removeDoorDashMarker();
      return;
    }

    const lngLat = getDoorDashMapCoords(doorDashRef.current);
    if (!lngLat) {
      removeDoorDashMarker();
      return;
    }

    if (doordashMarkerRef.current) {
      doordashMarkerRef.current.setLngLat(lngLat);
      return;
    }

    const doordashMarker = Radar.ui
      .marker({
        url: doordashIcon,
        width: "25px",
        height: "25px",
      })
      .setLngLat(lngLat)
      .addTo(map);

    markersRef.current.push(doordashMarker);
    doordashMarkerRef.current = doordashMarker;
  };

  useEffect(() => {
    if (!storeLocation?.lat || !storeLocation?.long) return;

    const map = Radar.ui.map({
      container: containerId,
      center: [storeLocation.long, storeLocation.lat],
      zoom: GOOGLE_MAP_DEFAULT_ZOOM,
      style: "radar-default-v1",
    });

    mapRef.current = map;

    map.on("load", () => {
      mapLoadedRef.current = true;

      const storeMarker = Radar.ui
        .marker({
          url: locationMarker,
          width: "28px",
          height: "28px",
        })
        .setLngLat([storeLocation.long, storeLocation.lat])
        .addTo(map);

      markersRef.current.push(storeMarker);

      if (pickUpMethod === DELIVERY && customerDetails?.address) {
        Radar.forwardGeocode({
          query: customerDetails.address,
        })
          .then((result) => {
            if (result.addresses && result.addresses.length > 0) {
              const { latitude, longitude } = result.addresses[0];

              const homeMarker = Radar.ui
                .marker({
                  url: homeIcon,
                  width: "20px",
                  height: "20px",
                })
                .setLngLat([longitude, latitude])
                .addTo(map);

              markersRef.current.push(homeMarker);
              syncDoorDashMarker(map);

              map.fitToMarkers({
                maxZoom: 14,
                padding: 80,
              });
            }
          })
          .catch((error) => {
            console.error("Geocoding error:", error);
          });
      } else if (pickUpMethod === DELIVERY) {
        syncDoorDashMarker(map);

        if (doordashMarkerRef.current) {
          map.fitToMarkers({
            maxZoom: 14,
            padding: 80,
          });
        }
      }
    });

    return () => {
      markersRef.current.forEach((marker) => {
        if (marker && marker.remove) {
          marker.remove();
        }
      });
      markersRef.current = [];
      doordashMarkerRef.current = null;
      mapLoadedRef.current = false;

      if (mapRef.current && mapRef.current.remove) {
        mapRef.current.remove();
      }
    };
  }, [storeLocation, customerDetails, pickUpMethod]);

  useEffect(() => {
    if (!mapRef.current || !mapLoadedRef.current) return;
    syncDoorDashMarker(mapRef.current);
  }, [
    doorDashMapCoords?.[0],
    doorDashMapCoords?.[1],
    pickUpMethod,
    doorDash?.status,
    (doorDash as { dasher_status?: string })?.dasher_status,
  ]);

  return (
    <div
      id={containerId}
      style={{
        width: "100%",
        height: "100%",
      }}
    />
  );
};

export default OrderTrackingMap;
